import createClient, { type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export type ErrorCode = NonNullable<NonNullable<Schemas['Error']['error']>['code']>;

/** An API error with the server's code and message, for showing to the user. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The server's id for this error (err_…), for "Report this problem" (APP-DESIGN.md 10.1). */
    readonly id: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Supplies the headers that identify the caller and the business on every request. */
export type AuthHeaders = () => Promise<Record<string, string>>;
let authHeaders: AuthHeaders = async () => ({});
let onUnauthenticated: () => void = () => {};

export function configureApi(headers: AuthHeaders, unauthenticated: () => void) {
  authHeaders = headers;
  onUnauthenticated = unauthenticated;
}

const auth: Middleware = {
  async onRequest({ request }) {
    for (const [name, value] of Object.entries(await authHeaders())) request.headers.set(name, value);
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) onUnauthenticated();
    return response;
  },
};

export const api = createClient<paths>({ baseUrl: '/' });
api.use(auth);

/** Throws an ApiError for a failed call and returns the `data` envelope of a successful one. */
export async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await call;
  if (error !== undefined || !response.ok) {
    const body = (error ?? {}) as { error?: { code?: string; message?: string; id?: string } };
    throw new ApiError(response.status, body.error?.code ?? 'internal_error', body.error?.message ?? `Request failed (${response.status})`, body.error?.id ?? null);
  }
  return data as T;
}

/** Fetches a binary response (an invoice PDF) with the same auth headers. */
export async function fetchBlob(path: string): Promise<Blob> {
  const response = await fetch(path, { headers: await authHeaders() });
  if (response.status === 401) onUnauthenticated();
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: { code?: string; message?: string; id?: string } };
    throw new ApiError(response.status, body.error?.code ?? 'internal_error', body.error?.message ?? `Request failed (${response.status})`, body.error?.id ?? null);
  }
  return response.blob();
}

/** Sends a multipart form (a file upload) with the same auth headers, returning the JSON body. */
export async function sendForm<T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const response = await fetch(path, { method, headers: await authHeaders(), body: form });
  if (response.status === 401) onUnauthenticated();
  const body = (await response.json().catch(() => ({}))) as { data?: T; error?: { code?: string; message?: string; id?: string } };
  if (!response.ok) {
    throw new ApiError(response.status, body.error?.code ?? 'internal_error', body.error?.message ?? `Request failed (${response.status})`, body.error?.id ?? null);
  }
  return body.data as T;
}

/**
 * POSTs JSON and reads the answer as server-sent events (the assistant, ADR 0047), calling `onEvent`
 * for each one as it arrives. Throws an ApiError when the request itself is refused.
 */
export async function streamPost(path: string, body: unknown, onEvent: (event: string, data: unknown) => void, signal?: AbortSignal): Promise<void> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  if (response.status === 401) onUnauthenticated();
  if (!response.ok || !response.body) {
    const err = (await response.json().catch(() => ({}))) as { error?: { code?: string; message?: string; id?: string } };
    throw new ApiError(response.status, err.error?.code ?? 'internal_error', err.error?.message ?? `Request failed (${response.status})`, err.error?.id ?? null);
  }
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let end: number;
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const parsed = parseSseBlock(block);
      if (parsed) onEvent(parsed.event, parsed.data);
    }
  }
}

/** One server-sent event block: its event name and JSON data, or null for a comment or keep-alive. */
export function parseSseBlock(block: string): { event: string; data: unknown } | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
  }
  if (data.length === 0) return null;
  try { return { event, data: JSON.parse(data.join('\n')) as unknown }; } catch { return null; }
}
